// ConsentWhitespace.tsx
import React, { useEffect, useState } from "react";
import IntellendLogo from "@/components/IntellendLogo";
import { cn } from "@/lib/utils";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FullScreenLoader from "@/components/FullScreenLoader";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import JourneyHeader from "@/components/JourneyHeader";

type ConsentDetails = {
  id: number;
  type: string;
  version: string;
};

const ConsentWhitespace = () => {
  const ua = navigator.userAgent || "";
  const isMobile = /\bandroid\b|iphone|ipad|ipod|mobile/i.test(ua);

  const { enc_id } = useParams<{ enc_id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // State for consent data
  const [content, setContent] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<boolean>(false);
  const [consentDetails, setConsentDetails] = useState<ConsentDetails | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Fetch consent data on mount
  useEffect(() => {
    if (!isMobile) {
      return;
    } // don’t fetch on desktop
    const fetchConsentData = async () => {
      setLoading(true);
      setError(null);

      // Step 1: Ensure cookie exists
      try {
        const searchParams = new URLSearchParams(location.search);
        const params = Object.fromEntries(searchParams.entries());
        const r1 = await axios.post(`${API_BASE_URL}/api/set_bap_identifier_cookie/`, {
          enc_id,
          ...params
        });
        console.log("Cookie set successfully:", r1.data);
      } catch (err: any) {
        let message: string = "ERROR: "
        if (err.response?.data) {
          message = message + err.response.data; // plain text from Django
        } else if (err.message) {
          message = message + err.message; // axios/network error
        }
        console.error(message);
        setError(message);
        setLoading(false);
        return;
      }

      // Step 2: Fetch consent details with retry logic for 404
      let retries = 0;
      const maxRetries = 5;

      while (retries < maxRetries) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/get_consent_details/`, {});
          console.log("Consent details:", response.data);

          const { consent_details, content, accepted, bap_name } = response.data;
          setConsentDetails(consent_details);
          let finalContent = content;
          if (bap_name && finalContent) {
            finalContent = finalContent.replaceAll("{bap_name}", bap_name);
          }
          setContent(finalContent);
          setLoading(false);
          return; // Success, exit the function
        } catch (err: any) {
          if (err.response?.status === 404 && retries < maxRetries - 1) {
            console.log(`404 error encountered. Retrying... (${retries + 1}/${maxRetries})`);
            retries++;
            // Add delay between retries (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retries));
          } else {
            window.location.reload();
            return; // Fail after max retries or non-404 error
          }
        }
      }

      // If we reach here, all retries failed
      setError("Failed to fetch consent details after multiple attempts. Please check your internet connectivity.");
      setLoading(false);
    };

    fetchConsentData()
  }, []);

  // Submit consent acceptance
  const handleSubmit = async () => {
    // If checkbox is not ticked, do nothing
    if (!accepted) return;
    if (!consentDetails) return;
    try {
      setSubmitting(true);
      // 2. Use value of checkbox (accepted) in API call
      await axios.post(`${API_BASE_URL}/api/submit_consent/`, {
        enc_id,
        accepts_terms_and_privacy: accepted,
        consent_details: consentDetails,
      });
      if (location.pathname.startsWith('/consent2/')) {
        navigate(`/index${location.search}`);
      } else {
        navigate(`/home${location.search}`);
      }
    } catch (err: any) {
      setError(err.message || "Failed to submit consent");
    } finally {
      setSubmitting(false);
    }
  };

  // CASE 1: Desktop/tablet (non-mobile) → Show QR screen
  if (!isMobile) {
    const currentUrl = window.location.href;
    return (
      <div className="fixed inset-0 bg-white flex flex-col justify-center items-center z-[9999]">
        <div className="flex flex-col items-center text-center px-4">
          <IntellendLogo size="md" />
          <h1 className="text-2xl font-semibold mt-6 mb-2 text-gray-800">
            Continue on Your Phone
          </h1>
          <p className="text-gray-600 mb-6 max-w-sm">
            Scan this QR code using your mobile camera to continue securely on your phone.
          </p>
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <QRCode value={currentUrl} size={220} />
          </div>
          <p className="text-sm text-gray-500 mt-4">
            This page can only be accessed from a mobile device.
          </p>
        </div>
      </div>
    );
  }

  // CASE 2: Mobile devices → Normal page flow
  if (loading) return <FullScreenLoader loading={loading} />;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Header with logo */}
      <JourneyHeader />

      {/* Consent content */}
      <main className="max-w-3xl mx-auto p-4">
        <h1 className="text-xl font-semibold mb-4 text-center">Data Sharing Consent</h1>

        <div
          className="border p-6 rounded-md bg-white shadow-sm text-gray-800 leading-relaxed font-medium text-lg"
          // Insert backend consent text safely
          dangerouslySetInnerHTML={{ __html: content || "" }}
        />

        {/* Acceptance controls */}
        <div className="mt-8 flex items-center gap-3">
          <input
            id="consent-checkbox-whitespace"
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="w-5 h-5 accent-intellend-green cursor-pointer"
          />
          <label htmlFor="consent-checkbox-whitespace" className="cursor-pointer text-gray-700 font-medium select-none">
            I agree to the above terms
          </label>
        </div>

        <Button
          onClick={handleSubmit}
          // 1. Disable if checkbox is not ticked OR submitting
          disabled={!accepted || submitting}
          className={cn(
            "w-full h-12 mt-8 text-lg font-semibold rounded-md text-white transition-all",
            (!accepted || submitting)
              ? "bg-gray-300 cursor-not-allowed opacity-70"
              : "bg-intellend-green hover:bg-green-700 shadow-md hover:shadow-lg"
          )}
        >
          {submitting ? "Processing..." : "Continue"}
        </Button>
      </main>
    </div>
  );
};

export default ConsentWhitespace;
